import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Card, PageHeader, EmptyState, ConnectorBadge } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, Database, ScrollText, Activity, Building2, Layers, Plus, Play, Landmark, CheckCircle2, Circle, GitBranch } from "lucide-react";
import { WorkflowTemplatesTab } from "@/pages/WorkflowAdmin";
import { AdvancedWorkflowsTab } from "@/pages/AdvancedWorkflowBuilder";

const inputCls = "h-10 rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink outline-none focus:border-brand-civic";
const selectCls = "h-10 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic";
const primaryBtn = "h-10 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy-700 inline-flex items-center justify-center gap-1 transition-colors";
const tabCls = "data-[state=active]:bg-brand-navy data-[state=active]:text-white text-slate-500 gap-1.5";

const ROLE_PILL = {
  administrator: "bg-brand-navy/10 text-brand-navy border-brand-navy/20",
  manager: "bg-brand-civic-50 text-brand-civic border-brand-civic/30",
  analyst: "bg-brand-teal-50 text-brand-teal border-brand-teal/30",
  viewer: "bg-slate-100 text-slate-500 border-slate-300",
};

function ReadOnlyProcessNotice() {
  return <EmptyState icon={GitBranch} title="Workflow administration is restricted"
    description="Only Administrators can create or modify workflow templates. You can see how an item's workflow behaves on any Insight." />;
}

function ProcessTab() {
  return (
    <Tabs defaultValue="advanced">
      <TabsList className="bg-white border border-brand-line">
        <TabsTrigger value="advanced" data-testid="process-subtab-advanced" className={tabCls}>Advanced Workflows</TabsTrigger>
        <TabsTrigger value="standard" data-testid="process-subtab-standard" className={tabCls}>Standard Workflows</TabsTrigger>
      </TabsList>
      <TabsContent value="advanced" className="mt-4"><AdvancedWorkflowsTab /></TabsContent>
      <TabsContent value="standard" className="mt-4"><WorkflowTemplatesTab /></TabsContent>
    </Tabs>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "administrator";
  const tabs = [["users", "Users", Users], ["reference", "Reference Data", Layers], ["process", "Process", GitBranch], ["governing", "Governing", Landmark], ["connectors", "Data Sources", Database], ["organization", "Organization", Building2]];
  tabs.push(["readiness", "Readiness", CheckCircle2], ["audit", "Audit Log", ScrollText], ["health", "System Health", Activity]);
  return (
    <div>
      <PageHeader title="Administration" subtitle="Manage users, reference data, governing bodies, audit log, and system health." />
      <Tabs defaultValue="users">
        <TabsList className="bg-white border border-brand-line flex-wrap h-auto">
          {tabs.map(([v, l, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className={tabCls}><Icon className="h-3.5 w-3.5" />{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="reference" className="mt-4"><ReferenceTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="process" className="mt-4">{isAdmin ? <ProcessTab /> : <ReadOnlyProcessNotice />}</TabsContent>
        <TabsContent value="governing" className="mt-4"><GoverningTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="connectors" className="mt-4"><ConnectorsTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="organization" className="mt-4"><OrgTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="readiness" className="mt-4"><ReadinessTab /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab /></TabsContent>
        <TabsContent value="health" className="mt-4"><HealthTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function GoverningTab({ isAdmin }) {
  const [bodies, setBodies] = useState(null);
  const [members, setMembers] = useState({});
  const [edit, setEdit] = useState({});
  const load = () => api.get("/governing-bodies").then((r) => setBodies(r.data.bodies || r.data.governing_bodies || r.data)).catch(() => setBodies(false));
  useEffect(() => { load(); }, []);
  const loadMembers = async (bid) => {
    if (members[bid]) { setMembers((m) => ({ ...m, [bid]: undefined })); return; }
    try { const r = await api.get(`/forum/board-memberships?governing_body_id=${bid}`); setMembers((m) => ({ ...m, [bid]: r.data.memberships })); }
    catch { setMembers((m) => ({ ...m, [bid]: [] })); }
  };
  const saveQuorum = async (bid) => {
    try { await api.put(`/governing-bodies/${bid}`, { quorum_required: Number(edit[bid]) }); toast.success("Quorum updated (prospective)"); setEdit((e) => ({ ...e, [bid]: undefined })); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed (administrator only)"); }
  };
  if (bodies === null) return <Loading />;
  if (!bodies || bodies.length === 0) return <EmptyState icon={Landmark} title="No Governing Bodies Yet" description="Create a governing body before configuring meeting types or board members." />;
  return (
    <div className="space-y-3" data-testid="governing-list">
      {bodies.map((b) => (
        <Card key={b.body_id} data-testid={`governing-${b.body_id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-brand-ink">{b.name} <span className="text-xs text-slate-400">· {b.code}</span></div>
              <div className="text-xs text-slate-500 mt-0.5">Quorum required: <strong className="text-brand-ink">{b.quorum_required ?? 3}</strong> voting members{b.active === false && " · Inactive"}</div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <input type="number" min="1" value={edit[b.body_id] ?? (b.quorum_required ?? 3)} onChange={(e) => setEdit((s) => ({ ...s, [b.body_id]: e.target.value }))}
                  data-testid={`quorum-input-${b.body_id}`} className="h-9 w-20 rounded-lg border border-brand-line px-2 text-sm" />
                <button onClick={() => saveQuorum(b.body_id)} data-testid={`quorum-save-${b.body_id}`} className="h-9 px-3 rounded-lg bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-700">Save Quorum</button>
              </div>
            )}
            <button onClick={() => loadMembers(b.body_id)} data-testid={`members-toggle-${b.body_id}`} className="h-9 px-3 rounded-lg border border-brand-line text-sm font-semibold text-brand-navy hover:bg-brand-bg">Members</button>
          </div>
          {members[b.body_id] && (
            <div className="mt-3 border-t border-brand-line pt-3 space-y-1.5" data-testid={`members-${b.body_id}`}>
              {members[b.body_id].length === 0 ? <p className="text-sm text-slate-400">No board members yet.</p> : members[b.body_id].map((m) => (
                <div key={m.membership_id} className="flex items-center justify-between text-sm rounded-lg bg-brand-bg border border-brand-line px-3 py-1.5">
                  <span className="text-brand-ink">{m.member_name} <span className="text-xs text-slate-400">· {m.role_label}{!m.voting_eligible && " · non-voting"}</span></span>
                  <span className="text-xs text-slate-400">{m.start_date || "—"} → {m.end_date || "present"}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function ReadinessTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/readiness").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <Loading />;
  if (!d) return <EmptyState icon={Activity} title="Unable to load readiness" />;
  return (
    <Card data-testid="readiness-panel" className="max-w-2xl">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-display font-semibold text-brand-ink">Configuration Readiness</h3>
        <span className={`ml-auto text-xs font-bold px-2.5 py-1 rounded-full ${d.ready ? "bg-brand-teal-50 text-brand-teal" : "bg-brand-gold-50 text-[#9A7212]"}`}>{d.ready ? "Ready" : "Setup Incomplete"}</span>
      </div>
      <div className="space-y-1.5">
        {d.checks.map((c) => (
          <div key={c.label} data-testid={`readiness-${c.label.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-3 rounded-lg border border-brand-line px-3 py-2">
            {c.status === "configured" ? <CheckCircle2 className="h-4 w-4 text-brand-teal shrink-0" />
              : c.status === "optional" ? <Circle className="h-4 w-4 text-slate-300 shrink-0" />
                : <Circle className="h-4 w-4 text-brand-gold shrink-0" />}
            <span className="text-sm font-medium text-brand-ink flex-1">{c.label}</span>
            <span className={`text-xs ${c.status === "configured" ? "text-brand-teal" : c.status === "optional" ? "text-slate-400" : "text-[#9A7212]"}`}>{c.detail || c.status}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-4">This reflects customer configuration readiness, not runtime infrastructure.</p>
    </Card>
  );
}

function RolePill({ role }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${ROLE_PILL[role] || ROLE_PILL.viewer}`}>{role}</span>;
}

function UsersTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "viewer", group_id: "" });
  const load = () => api.get("/admin/users").then((r) => setData(r.data.users)).catch(() => setData(false));
  useEffect(() => { load(); api.get("/meta").then((r) => setMeta(r.data)); }, []);
  const create = async () => {
    if (!form.email || !form.name) return toast.error("Email and name required");
    try { await api.post("/admin/users", { ...form, group_id: form.group_id || null }); toast.success("User created"); setForm({ email: "", name: "", password: "", role: "viewer", group_id: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const update = async (id, patch) => { try { await api.put(`/admin/users/${id}`, patch); toast.success("Updated"); load(); } catch { toast.error("Failed"); } };
  if (data === null) return <Loading />;
  return (
    <div className="space-y-4">
      {isAdmin && (
        <Card data-testid="create-user-form">
          <h3 className="font-display font-semibold text-brand-ink mb-3">Add user</h3>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            <input data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
            <input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} />
            <input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temp password" className={inputCls} />
            <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={selectCls}>{(meta?.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}</select>
            <button onClick={create} data-testid="create-user-submit" className={primaryBtn}><Plus className="h-4 w-4" /> Add</button>
          </div>
        </Card>
      )}
      <div className="rounded-xl border border-brand-line overflow-hidden bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase border-b border-brand-line"><tr><th className="text-left px-4 py-3 font-semibold">Name</th><th className="text-left px-4 py-3 font-semibold">Email</th><th className="text-left px-4 py-3 font-semibold">Role</th><th className="text-left px-4 py-3 font-semibold">Status</th></tr></thead>
            <tbody className="divide-y divide-brand-line">
              {data.map((u) => (
                <tr key={u.user_id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3 text-brand-ink font-medium">{u.name} {u.is_sample && <span className="text-[10px] text-brand-civic">(sample)</span>}</td>
                  <td className="px-4 py-3 text-slate-500">{u.email}</td>
                  <td className="px-4 py-3">{isAdmin ? <select value={u.role} onChange={(e) => update(u.user_id, { role: e.target.value })} data-testid={`role-select-${u.user_id}`} className="h-8 rounded bg-white border border-brand-line px-2 text-sm text-brand-ink">{(meta?.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}</select> : <RolePill role={u.role} />}</td>
                  <td className="px-4 py-3">{isAdmin ? <button onClick={() => update(u.user_id, { is_active: !u.is_active })} className={`text-xs px-2 py-1 rounded font-medium ${u.is_active ? "bg-brand-teal-50 text-brand-teal" : "bg-red-50 text-red-600"}`} data-testid={`toggle-active-${u.user_id}`}>{u.is_active ? "Active" : "Disabled"}</button> : <span className="text-slate-500">{u.is_active ? "Active" : "Disabled"}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReferenceTab({ isAdmin }) {
  const kinds = [["groups", "Policy Units / Groups"], ["policy-areas", "Policy Areas"], ["committees", "Committees"], ["sessions", "Legislative Sessions"]];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {kinds.map(([k, label]) => <RefBlock key={k} kind={k} label={label} isAdmin={isAdmin} />)}
    </div>
  );
}
function RefBlock({ kind, label, isAdmin }) {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get(`/admin/ref/${kind}`).then((r) => setItems(r.data.items)).catch(() => setItems(false));
  useEffect(() => { load(); }, [kind]);
  const add = async () => { if (!name) return; try { await api.post(`/admin/ref/${kind}`, { name }); toast.success("Added"); setName(""); load(); } catch { toast.error("Failed"); } };
  return (
    <Card data-testid={`ref-${kind}`}>
      <h3 className="font-display font-semibold text-brand-ink mb-3">{label}</h3>
      {items === null ? <Loading /> : (
        <div className="space-y-1.5 mb-3 max-h-52 overflow-y-auto">{(items || []).map((it, i) => <div key={i} className="flex items-center gap-2 text-sm text-slate-700 px-3 py-1.5 rounded bg-brand-bg border border-brand-line">{it.name}{it.is_sample && <span className="text-[10px] text-brand-civic ml-auto">sample</span>}</div>)}</div>
      )}
      {isAdmin && <div className="flex gap-2"><input value={name} onChange={(e) => setName(e.target.value)} placeholder={`New ${label.toLowerCase()}…`} data-testid={`ref-input-${kind}`} className="flex-1 h-9 rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink outline-none focus:border-brand-civic" /><button onClick={add} data-testid={`ref-add-${kind}`} className="h-9 px-3 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy-700">Add</button></div>}
    </Card>
  );
}

function ConnectorsTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);
  const [running, setRunning] = useState("");
  const load = () => api.get("/admin/data/connectors").then((r) => setData(r.data.connectors)).catch(() => setData(false));
  useEffect(() => { load(); api.get("/admin/data/mode").then((r) => setMode(r.data.data_mode)).catch(() => {}); }, []);
  const run = async (key) => {
    setRunning(key);
    try {
      const r = await api.post(`/admin/data/connectors/${key}/run?limit=15`);
      const x = r.data.result;
      if (x?.degraded) toast.warning(`${key} degraded: ${x.message?.slice(0, 90)}`);
      else toast.success(`Ingest complete: ${x.created} new, ${x.updated} updated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ingestion failed");
    } finally { setRunning(""); }
  };
  const setEnabled = async (key, enabled) => {
    try { await api.post(`/admin/data/connectors/${key}/enable`, { enabled }); toast.success(`${key} ${enabled ? "enabled" : "disabled"}`); load(); }
    catch { toast.error("Failed (admin only)"); }
  };
  const setDataMode = async (m) => { try { await api.put("/admin/data/mode", { data_mode: m }); setMode(m); toast.success(`Data mode: ${m.toUpperCase()}`); } catch { toast.error("Failed (admin only)"); } };
  if (data === null) return <Loading />;
  return (
    <div>
      <Card className="mb-4" data-testid="data-mode-control">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold text-brand-ink">Live vs Sample data</h3>
            <p className="text-xs text-slate-500 mt-1">Production defaults to <strong className="text-brand-teal">LIVE</strong>. Sample records never appear to users as real legislation.</p>
          </div>
          <div className="flex rounded-lg border border-brand-line overflow-hidden bg-white">
            {["live", "both", "sample"].map((m) => (
              <button key={m} onClick={() => isAdmin && setDataMode(m)} disabled={!isAdmin} data-testid={`data-mode-${m}`}
                className={`px-4 h-9 text-sm capitalize disabled:opacity-60 transition-colors ${mode === m ? "bg-brand-navy text-white font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>{m}</button>
            ))}
          </div>
        </div>
      </Card>
      <p className="text-sm text-slate-500 mb-4">Official Florida government connectors. Disabled connectors never fabricate data. Sources that cannot be reliably automated are marked <span className="text-brand-gold font-semibold">DEGRADED</span> instead of inventing records.</p>
      <div className="space-y-2" data-testid="connectors-list">
        {data.map((c) => <ConnectorCard key={c.key} c={c} isAdmin={isAdmin} running={running} onRun={run} onEnable={setEnabled} />)}
      </div>
      <AgencySources isAdmin={isAdmin} />
    </div>
  );
}

function ConnectorCard({ c, isAdmin, running, onRun, onEnable }) {
  const [panel, setPanel] = useState("");
  const [detail, setDetail] = useState(null);
  const openPanel = async (which) => {
    if (panel === which) { setPanel(""); return; }
    setPanel(which); setDetail(null);
    const url = which === "errors" ? `/admin/data/connectors/${c.key}/errors` : `/admin/data/connectors/${c.key}/activity`;
    try { const r = await api.get(url); setDetail(r.data); } catch { setDetail(false); }
  };
  const healthColor = c.health === "healthy" ? "bg-brand-teal-50 text-brand-teal" : c.health === "degraded" ? "bg-brand-gold-50 text-[#9A7212]" : c.health === "failed" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500";
  return (
    <div className="rounded-xl border border-brand-line bg-white p-4 shadow-card" data-testid={`connector-${c.key}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-brand-ink">{c.name}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase">{(c.connector_type || "").replace(/_/g, " ")}</span>
          <ConnectorBadge enabled={c.enabled} />
          {c.enabled && <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${healthColor}`}>{c.health}</span>}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <button onClick={() => onEnable(c.key, !c.enabled)} data-testid={`toggle-connector-${c.key}`} className="h-8 px-2.5 rounded-lg border border-brand-line bg-white text-xs text-brand-navy font-medium hover:border-brand-civic/50 hover:bg-brand-civic-50">{c.enabled ? "Disable" : "Enable"}</button>}
          {isAdmin && c.enabled && <button onClick={() => onRun(c.key)} disabled={running === c.key} data-testid={`run-connector-${c.key}`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-brand-line bg-white text-xs text-brand-navy font-medium hover:border-brand-civic/50 hover:bg-brand-civic-50 disabled:opacity-50"><Play className="h-3.5 w-3.5" /> {running === c.key ? "Running…" : "Run now"}</button>}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-1">Source: {c.base_url} · Host: {c.allowed_host || "—"} · Refresh: {c.refresh_interval}</p>
      {c.enabled && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mt-3 text-xs" data-testid={`connector-health-${c.key}`}>
          <Health label="Processed" value={c.records_processed} />
          <Health label="Created" value={c.records_created} />
          <Health label="Updated" value={c.records_updated} />
          <Health label="Errors" value={c.error_count} alert={c.error_count > 0} />
          <Health label="Last attempt" value={c.last_attempt ? fmtDate(c.last_attempt) : "—"} />
          <Health label="Last success" value={c.last_success ? fmtDate(c.last_success) : "—"} />
          <Health label="Next run" value={c.next_scheduled_run ? fmtDate(c.next_scheduled_run) : "—"} />
        </div>
      )}
      {c.last_error && <p className="text-xs text-brand-gold mt-2 line-clamp-2">Last note: {c.last_error}</p>}
      {c.enabled && (
        <div className="flex gap-3 mt-2">
          <button onClick={() => openPanel("errors")} data-testid={`errors-btn-${c.key}`} className="text-xs font-medium text-slate-500 hover:text-brand-navy">Recent errors</button>
          <button onClick={() => openPanel("activity")} data-testid={`activity-btn-${c.key}`} className="text-xs font-medium text-slate-500 hover:text-brand-navy">Recent activity</button>
        </div>
      )}
      {panel && (
        <div className="mt-2 rounded-lg bg-brand-bg border border-brand-line p-3 text-xs" data-testid={`panel-${panel}-${c.key}`}>
          {detail === null ? "Loading…" : panel === "errors" ? (
            (detail.recent_errors || []).length ? detail.recent_errors.map((e, i) => <div key={i} className="text-red-600">{e}</div>) : <span className="text-slate-400">No recent errors.</span>
          ) : (
            (detail.hub_activity || []).length || (detail.recent_bill_actions || []).length ?
              [...(detail.hub_activity || []).map((a, i) => <div key={"h" + i} className="text-slate-600">{a.action}: {a.title}</div>),
               ...(detail.recent_bill_actions || []).slice(0, 5).map((a, i) => <div key={"b" + i} className="text-slate-500">{a.description}</div>)]
              : <span className="text-slate-400">No recent activity.</span>
          )}
        </div>
      )}
    </div>
  );
}

function AgencySources({ isAdmin }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ agency_code: "", agency_name: "", source_name: "", base_url: "", ingestion_method: "rss", allowed_hosts: "" });
  const load = () => api.get("/admin/data/agencies").then((r) => setItems(r.data.agencies)).catch(() => setItems(false));
  useEffect(() => { load(); }, []);
  const toggle = async (code, enabled) => { try { await api.put(`/admin/data/agencies/${code}`, { enabled }); toast.success("Updated"); load(); } catch { toast.error("Failed"); } };
  const add = async () => {
    if (!form.agency_code || !form.base_url) return toast.error("Code and base URL required");
    try { await api.post("/admin/data/agencies", { ...form, allowed_hosts: form.allowed_hosts.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Agency source added"); setForm({ agency_code: "", agency_name: "", source_name: "", base_url: "", ingestion_method: "rss", allowed_hosts: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (items === null) return null;
  return (
    <Card className="mt-6" data-testid="agency-framework">
      <h3 className="font-display font-semibold text-brand-ink mb-1">State Agency Connector Framework</h3>
      <p className="text-xs text-slate-500 mb-3">Add agency sources by configuration (RSS / HTML listing) — no redevelopment required.</p>
      <div className="space-y-2 mb-4">
        {(items || []).map((a) => (
          <div key={a.agency_code} className="flex items-center justify-between gap-2 rounded-lg bg-brand-bg border border-brand-line px-3 py-2" data-testid={`agency-${a.agency_code}`}>
            <div><span className="text-sm font-medium text-brand-ink">{a.agency_name}</span> <span className="text-xs text-slate-400">· {a.source_name} · {a.ingestion_method} · {a.health_status}</span></div>
            {isAdmin && <button onClick={() => toggle(a.agency_code, !a.enabled)} data-testid={`agency-toggle-${a.agency_code}`} className={`text-xs px-2 py-1 rounded font-medium ${a.enabled ? "bg-brand-teal-50 text-brand-teal" : "bg-slate-100 text-slate-500"}`}>{a.enabled ? "Enabled" : "Disabled"}</button>}
          </div>
        ))}
      </div>
      {isAdmin && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2" data-testid="agency-add-form">
          <input value={form.agency_code} onChange={(e) => setForm({ ...form, agency_code: e.target.value })} placeholder="Code" className="h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic" data-testid="agency-code" />
          <input value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })} placeholder="Agency name" className="h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic" />
          <input value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} placeholder="Source name" className="h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic" />
          <input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://…" className="h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic" data-testid="agency-url" />
          <select value={form.ingestion_method} onChange={(e) => setForm({ ...form, ingestion_method: e.target.value })} className="h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic">{["rss", "html_listing", "json", "xml"].map((m) => <option key={m} value={m}>{m}</option>)}</select>
          <button onClick={add} data-testid="agency-add-submit" className="h-9 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy-700">Add</button>
        </div>
      )}
    </Card>
  );
}
function Health({ label, value, alert }) {
  return <div className="rounded-lg bg-brand-bg border border-brand-line px-2 py-1.5"><div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div><div className={`font-mono ${alert ? "text-red-600" : "text-brand-ink"}`}>{value}</div></div>;
}

function OrgTab({ isAdmin }) {
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/organization").then((r) => setOrg(r.data)).catch(() => setOrg(false)); }, []);
  const save = async () => { try { await api.put("/organization", org); toast.success("Organization updated"); } catch { toast.error("Failed"); } };
  if (org === null) return <Loading />;
  if (!org) return <EmptyState title="Unable to load organization" />;
  return (
    <Card data-testid="org-form" className="max-w-xl">
      <h3 className="font-display font-semibold text-brand-ink mb-1">Organization & Branding</h3>
      <p className="text-xs text-slate-500 mb-4">Multi-tenant configuration — this platform is not hard-coded to any single customer.</p>
      <div className="space-y-3">
        {[["name", "Organization name"], ["brand_short", "Short brand"], ["jurisdiction", "Jurisdiction"], ["primary_color", "Primary color (hex)"], ["logo_url", "Logo URL"]].map(([k, label]) => (
          <div key={k}><label className="text-xs font-medium text-slate-500">{label}</label><input value={org[k] || ""} disabled={!isAdmin} onChange={(e) => setOrg({ ...org, [k]: e.target.value })} data-testid={`org-${k}`} className="mt-1 w-full h-10 rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink outline-none focus:border-brand-civic disabled:opacity-60 disabled:bg-brand-bg" /></div>
        ))}
      </div>
      {isAdmin && <button onClick={save} data-testid="org-save" className="mt-4 h-10 px-4 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy-700">Save changes</button>}
    </Card>
  );
}

function AuditTab() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const load = () => { setData(null); api.get(`/admin/audit${cat ? `?category=${cat}` : ""}`).then((r) => setData(r.data.logs)).catch(() => setData(false)); };
  useEffect(() => { load(); }, [cat]);
  const cats = ["auth", "assignment", "bill_review", "internal_note", "alert_config", "report_export", "user_admin", "system_config"];
  const pill = (on) => `text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${on ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-brand-line text-slate-500 hover:border-brand-civic/40 hover:text-brand-navy"}`;
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        <button onClick={() => setCat("")} className={pill(!cat)}>All</button>
        {cats.map((c) => <button key={c} onClick={() => setCat(c)} data-testid={`audit-cat-${c}`} className={pill(cat === c)}>{c}</button>)}
      </div>
      {data === null ? <Loading /> : !data || data.length === 0 ? <EmptyState icon={ScrollText} title="No audit entries" /> : (
        <div className="rounded-xl border border-brand-line overflow-hidden bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase border-b border-brand-line"><tr><th className="text-left px-4 py-2 font-semibold">When</th><th className="text-left px-4 py-2 font-semibold">Actor</th><th className="text-left px-4 py-2 font-semibold">Category</th><th className="text-left px-4 py-2 font-semibold">Action</th><th className="text-left px-4 py-2 font-semibold">Target</th></tr></thead>
              <tbody className="divide-y divide-brand-line">
                {data.map((l) => <tr key={l.log_id} className="hover:bg-slate-50 transition-colors"><td className="px-4 py-2 text-slate-500 whitespace-nowrap">{fmtDate(l.created_at)}</td><td className="px-4 py-2 text-slate-700">{l.actor_email}</td><td className="px-4 py-2"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{l.category}</span></td><td className="px-4 py-2 text-slate-700">{l.action}</td><td className="px-4 py-2 text-slate-400 font-mono text-xs">{l.target}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function HealthTab() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/admin/health/system").then((r) => setH(r.data)).catch(() => setH(false)); }, []);
  if (h === null) return <Loading />;
  if (!h) return <EmptyState icon={Activity} title="Unable to load health" />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="system-health">
      <Card>
        <h3 className="font-display font-semibold text-brand-ink mb-3">Service status</h3>
        <div className="flex items-center gap-2 mb-4"><span className={`h-2.5 w-2.5 rounded-full ${h.database === "connected" ? "bg-brand-teal" : "bg-red-500"}`} /><span className="text-slate-700">Database: {h.database}</span></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-brand-civic-50 border border-brand-civic/20 px-3 py-2"><div className="font-display text-2xl font-bold text-brand-civic">{h.sample_bills}</div><div className="text-xs text-slate-500">Sample bills</div></div>
          <div className="rounded-lg bg-brand-teal-50 border border-brand-teal/20 px-3 py-2"><div className="font-display text-2xl font-bold text-brand-teal">{h.live_bills}</div><div className="text-xs text-slate-500">Live bills</div></div>
          <div className="rounded-lg bg-brand-gold-50 border border-brand-gold/20 px-3 py-2"><div className="font-display text-2xl font-bold text-[#9A7212]">{h.enabled_connectors}/{h.total_connectors}</div><div className="text-xs text-slate-500">Connectors enabled</div></div>
        </div>
      </Card>
      <Card>
        <h3 className="font-display font-semibold text-brand-ink mb-3">Collection counts</h3>
        <div className="space-y-2">{Object.entries(h.collections).map(([k, v]) => <div key={k} className="flex justify-between text-sm border-b border-brand-line/60 pb-1.5"><span className="text-slate-500 capitalize">{k}</span><span className="text-brand-ink font-mono font-semibold">{v}</span></div>)}</div>
      </Card>
    </div>
  );
}
